import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Ship } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { BadgeEstatus, type Estatus } from "@/componentes/expediente/BadgeEstatus"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/**
 * Portal del importador: sus despachos, en sus términos.
 *
 * Es la MISMA API que la bandeja interna — el recorte por cliente lo hace el
 * servidor en `aplicar_alcance_cliente`, no esta pantalla. Aquí no hay ningún
 * filtro de seguridad, y no debe haberlo: una comprobación en el navegador se
 * salta abriendo la consola.
 *
 * Lo que sí cambia es el **lenguaje y la densidad**. El tramitador quiere once
 * estatus, tasas y números de expediente; el importador quiere saber dónde está su
 * carga y qué le falta entregar. Enseñarle la bandeja interna «porque los datos
 * son los mismos» es el error clásico de los portales de cliente.
 */

interface FilaDespacho {
  id: number
  numero: string
  estado: "ABIERTO" | "CERRADO"
  estatus_operativo: Estatus
  fecha_apertura: string
  fecha_cierre: string | null
}

interface Pagina {
  total: number
  limit: number
  offset: number
  items: FilaDespacho[]
}

const POR_PAGINA = 25

export function MisDespachos() {
  const [offset, setOffset] = useState(0)
  const [soloAbiertos, setSoloAbiertos] = useState(true)

  const parametros = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(offset) })
  if (soloAbiertos) parametros.set("estado", "ABIERTO")

  const consulta = useQuery({
    queryKey: ["portal-despachos", { offset, soloAbiertos }],
    queryFn: () => api.get<Pagina>(`/api/v1/expedientes?${parametros}`),
    // Sondeo de 60 s: el importador deja esta pantalla abierta esperando noticias.
    // La tabla NO se anima al refrescar, o temblaría cada minuto.
    refetchInterval: 60_000,
  })

  const pagina = consulta.data
  const hasta = pagina ? Math.min(pagina.offset + pagina.limit, pagina.total) : 0

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold">Mis despachos</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          {pagina ? `${pagina.total} ${pagina.total === 1 ? "despacho" : "despachos"}` : " "}
        </p>
      </div>

      <label className="mb-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={soloAbiertos}
          onChange={(e) => {
            setSoloAbiertos(e.target.checked)
            setOffset(0)
          }}
        />
        Ver sólo los que siguen en curso
      </label>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando sus despachos…
        </p>
      )}

      {consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <div>
            <p className="font-medium">No se pudieron cargar sus despachos</p>
            <p className="mt-1 text-sm">
              {consulta.error instanceof ErrorApi
                ? consulta.error.message
                : "Error inesperado de red"}
            </p>
          </div>
        </div>
      )}

      {pagina && pagina.items.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <Ship size={24} aria-hidden="true" />
          <p className="text-sm">
            {soloAbiertos ? "No tiene despachos en curso" : "Todavía no hay despachos registrados"}
          </p>
        </div>
      )}

      {pagina && pagina.items.length > 0 && (
        <Tabla>
          <Cabecera>
            <Th>Despacho</Th>
            <Th>Situación</Th>
            <Th derecha>Abierto el</Th>
          </Cabecera>
          <Cuerpo>
            {pagina.items.map((fila) => (
              <Fila key={fila.id}>
                <Td>
                  <Link to={`/portal/${fila.id}`} className="font-medium underline">
                    {fila.numero}
                  </Link>
                </Td>
                <Td>
                  <BadgeEstatus estatus={fila.estatus_operativo} />
                </Td>
                <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                  {comoFecha(fila.fecha_apertura)}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}

      {pagina && pagina.total > pagina.limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="tabular" style={{ color: "var(--fg-muted)" }}>
            {pagina.offset + 1}–{hasta} de {pagina.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={hasta >= pagina.total}
              onClick={() => setOffset(offset + POR_PAGINA)}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
